// The admin's form window (APS-412, admin-d.md "Forms"): a `.win.form` opened under the page head
// or inside its section, never a page of its own. Its bar names the action; `.form-grid` holds
// the fields (two columns, one on a phone; a textarea and the actions span the row); the
// actions sit at the bottom left: the primary, solid (the danger `.btn` for what cannot be taken
// back, as the console's Danger zone), and Cancelar, ghost. Every staff action carries its required Motivo
// (ReasonField), which the API writes to the admin audit log.
import { type FormEvent, type ReactNode, useId } from 'react'
import { useI18n } from '../../i18n'

export function FormWindow({
  title,
  submitLabel,
  danger,
  busy,
  submitDisabled,
  onSubmit,
  onCancel,
  alert,
  children,
}: {
  title: string
  submitLabel: string
  /** The primary in the danger tone: an erasure, a deletion, a suspension. */
  danger?: boolean | undefined
  busy?: boolean | undefined
  /** The primary alone off (a conflict to reload first); Cancelar stays. */
  submitDisabled?: boolean | undefined
  onSubmit: (e: FormEvent<HTMLFormElement>) => void
  onCancel: () => void
  /** A refusal or an API error, right above the actions. */
  alert?: ReactNode
  children: ReactNode
}) {
  const { t } = useI18n()
  const titleId = useId()
  return (
    <form className="win form" onSubmit={onSubmit} aria-labelledby={titleId}>
      <div className="win-bar">
        <b id={titleId}>{title}</b>
      </div>
      <div className="form-grid">
        {children}
        {alert}
        <div className="acts">
          <button
            type="submit"
            className={danger ? 'btn danger solid' : 'btn solid'}
            disabled={busy || submitDisabled}
          >
            {submitLabel}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
            {t('action.cancel')}
          </button>
        </div>
      </div>
    </form>
  )
}

/** The required Motivo: a textarea across the row, with where the reason is kept under it. */
export function ReasonField({
  value,
  onChange,
  label,
  hint,
  optional,
}: {
  value: string
  onChange: (value: string) => void
  label?: string | undefined
  /** Under the field; several short lines when it says several things (APS-468, Mosaic). */
  hint?: ReactNode
  optional?: boolean | undefined
}) {
  const { t } = useI18n()
  const id = useId()
  return (
    <div className="wide field-d">
      <label className="lbl" htmlFor={id}>
        {label ?? t('action.reason')}
      </label>
      <textarea
        id={id}
        name="reason"
        className="input"
        rows={2}
        maxLength={1000}
        required={!optional}
        aria-describedby={`${id}-hint`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="hint" id={`${id}-hint`}>
        {hint ?? t('action.reasonHint')}
      </span>
    </div>
  )
}
